import { isLoading } from "apollo/utils";
import clsx from "clsx";
import {
  Auction,
  DocsFieldFeedback,
  DocsFormDataMapped,
  DocsFormName,
  QueryProgress,
} from "core/types";
import { useTranslations } from "translations";
import { FormFieldConfig } from "./Forms";
import { HeaderBannerAction, HeaderBanners } from "./HeaderBanners";
import { SourceInformation } from "./useDocsForm";
import { SourceWrapper } from "./utils";

export function DocsFormFields<T extends DocsFormName>({
  auction,
  config,
  formName,
  headerBannerAction,
  importData,
  importFormProgress,
  onCloseHeaderBanner,
  onFieldFeedback,
  shouldSkipRequiredValidation,
  sourceInformation,
}: {
  auction: Auction | undefined;
  config: FormFieldConfig<T>;
  formName: T;
  headerBannerAction: HeaderBannerAction;
  importData: () => void;
  importFormProgress: QueryProgress;
  onCloseHeaderBanner: () => void;
  onFieldFeedback?: (feedback: DocsFieldFeedback | null) => void;
  shouldSkipRequiredValidation: boolean;
  sourceInformation: SourceInformation<T>;
}) {
  const translations = useTranslations();

  if (!config.sections) return null;

  return (
    <div className="flex h-full flex-col bg-gray-50">
      {headerBannerAction.banner && (
        <div className="bg-gray-50 px-4 pt-4 pb-1">
          <HeaderBanners
            headerBannerAction={headerBannerAction}
            importData={importData}
            onClose={onCloseHeaderBanner}
          />
        </div>
      )}
      <div className="flex-1 overflow-y-auto">
        <div
          className={clsx(
            "flex w-full flex-col gap-4 bg-gray-50 px-4 pt-4 pb-4 transition-opacity",
            { "opacity-50": isLoading(importFormProgress) },
          )}
        >
          {config.sections
            .sort((a, b) => a.position - b.position)
            .map((section) => (
              <div
                key={section.position}
                className="bt-1 flex w-full flex-col rounded-lg bg-white shadow-sm"
              >
                {section.title &&
                  (() => {
                    const titleContent = section.title({ translations });
                    return typeof titleContent === "string" ? (
                      <h2 className="w-full px-4 py-4 text-heading-lg font-bold">
                        {titleContent}
                      </h2>
                    ) : (
                      titleContent
                    );
                  })()}
                <hr className="w-full border-gray-400" />
                <div className="flex flex-col items-center gap-2 px-4 py-4 pt-0">
                  {section.description && (
                    <p className="w-full pt-4 pb-2 text-body-md">
                      {section.description({ translations })}
                    </p>
                  )}
                  {(
                    Object.keys(section.fields) as Array<
                      keyof DocsFormDataMapped<T>
                    >
                  )
                    .sort((a, b) => {
                      const aField = section.fields[a];
                      const bField = section.fields[b];
                      if (!aField || !bField) return 0;
                      return aField.position - bField.position;
                    })
                    .map((fieldName) => {
                      const field = section.fields[fieldName];
                      if (!field) return null;

                      const subTitleContent = field.subTitle
                        ? field.subTitle.text({ translations })
                        : null;
                      const hasStringSubTitle =
                        subTitleContent !== null &&
                        typeof subTitleContent === "string";
                      const ariaLabelledById = hasStringSubTitle
                        ? `docs-form-subtitle-${formName}-${String(fieldName)}`
                        : undefined;
                      const showAsterisk =
                        !!field.subTitle?.isRequired &&
                        !shouldSkipRequiredValidation;

                      return (
                        <div
                          key={String(fieldName)}
                          className="flex w-full flex-col gap-2"
                        >
                          {field.subTitle &&
                            (hasStringSubTitle ? (
                              <h3
                                className={clsx(
                                  "w-full pt-4 text-heading-md font-bold",
                                  field.subTitle.className,
                                  showAsterisk &&
                                    "after:ml-0.5 after:text-gray-900 after:content-['*']",
                                )}
                                id={ariaLabelledById}
                              >
                                {subTitleContent}
                              </h3>
                            ) : (
                              subTitleContent
                            ))}
                          {field.description && (
                            <p className="w-full px-4 text-body-md">
                              {field.description({ translations })}
                            </p>
                          )}
                          <SourceWrapper<T>
                            fieldName={fieldName}
                            formName={formName}
                            hideSource={field.hideSource}
                            sourceInformation={sourceInformation}
                          >
                            {field.input({
                              ariaLabelledById,
                              auction,
                              fieldRating:
                                sourceInformation.getSourceFields(fieldName)
                                  ?.rating,
                              formName,
                              onFieldFeedback,
                              schemaVersion: config.schemaVersion,
                              skipRequiredValidation:
                                shouldSkipRequiredValidation,
                              sourceInformation,
                              translations,
                            })}
                          </SourceWrapper>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
